import { Link, useParams } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articles } from '@/mocks/photography';

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-20 pb-16 px-6 text-center">
          <h1 className="text-xl text-foreground-700">文章未找到</h1>
          <Link to="/articles" className="text-primary-500 text-sm mt-4 inline-block">返回文章列表</Link>
        </div>
        <Footer />
      </>
    );
  }

  const baseUrl = window.location.origin + (window.__BASE_PATH__ || '');

  const articleSEO = {
    title: `${article.title} | 秀人写真摄影指南`,
    description: article.excerpt,
    keywords: article.tags.join(',') + ',秀人写真,摄影指南',
    canonicalUrl: `/article/${article.slug}`,
    ogType: 'article',
    ogImage: article.coverImage,
  };

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '秀人写真',
    },
    url: `${baseUrl}/article/${article.slug}`,
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: baseUrl + '/' },
      { '@type': 'ListItem', position: 2, name: '摄影指南', item: baseUrl + '/articles' },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      <SEOHead data={articleSEO} />
      <ScrollToTop />
      <Navbar />
      <main>
        <article className="pt-20 pb-16 px-6 md:px-10 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <Breadcrumb
              items={[
                { name: '首页', path: '/' },
                { name: '摄影指南', path: '/articles' },
                { name: article.title },
              ]}
            />

            <header className="mt-6 mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-400">{article.date}</span>
                <span className="text-xs text-foreground-400">{article.readTime}</span>
              </div>
              <h1 className="text-xl md:text-2xl font-heading text-foreground-950 leading-snug mb-4">
                {article.title}
              </h1>
              <p className="text-sm text-foreground-500 leading-relaxed">{article.excerpt}</p>
              <div className="flex items-center gap-2 mt-4 text-xs text-foreground-400">
                <span>作者：{article.author}</span>
              </div>
            </header>

            <div className="mb-8 rounded-xl overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-auto object-cover"
                title={article.title}
              />
            </div>

            <div
              className="prose prose-sm max-w-none text-foreground-700 leading-relaxed [&>h2]:text-lg [&>h2]:font-heading [&>h2]:text-foreground-950 [&>h2]:mt-8 [&>h2]:mb-4 [&>p]:mb-4 [&>p]:text-sm [&>strong]:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            ></div>

            <div className="mt-10 pt-6 border-t border-background-200">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-background-100 text-xs text-foreground-600"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-8 text-center">
              <Link
                to="/articles"
                className="inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors font-medium cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i> 返回文章列表
              </Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}